/**
 * Case-insensitive picklist key/label comparison (matches object.model picklist resolution).
 */

/** Normalize a picklist value for case-insensitive comparison. */
export function normalizePicklistCompareKey(v) {
  if (v == null || v === '') {
    return '';
  }
  return String(v).trim().toLowerCase();
}

/** Case-insensitive equality for picklist keys/labels. */
export function picklistValuesEqual(a, b) {
  const na = normalizePicklistCompareKey(a);
  const nb = normalizePicklistCompareKey(b);
  if (!na || !nb) {
    return false;
  }
  return na === nb;
}

/** Comparable tokens for a picklist option (key, value, label, API aliases). */
export function picklistOptionTokens(option) {
  if (!option) {
    return [];
  }
  return [
    option.key,
    option.value_key,
    option.value,
    option.value_label,
    option.label,
  ]
    .filter((v) => v != null && String(v).trim() !== '')
    .map((v) => normalizePicklistCompareKey(v));
}

/** Whether a picklist option matches a stored/current value (case-insensitive). */
export function picklistOptionMatchesValue(option, currentValue) {
  const cv = normalizePicklistCompareKey(currentValue);
  if (!cv) {
    return false;
  }
  return picklistOptionTokens(option).includes(cv);
}

/** Find picklist option matching value (case-insensitive). */
export function findPicklistOptionByValue(options, currentValue) {
  if (!options?.length) {
    return null;
  }
  const cv = normalizePicklistCompareKey(currentValue);
  if (!cv) {
    return null;
  }
  return options.find((opt) => picklistOptionMatchesValue(opt, cv)) || null;
}

/** Canonical storage key for a picklist option (prefers key/value_key). */
export function picklistOptionStorageKey(option) {
  if (!option) {
    return '';
  }
  const k = option.key ?? option.value_key;
  if (k != null && String(k).trim() !== '') {
    return String(k).trim();
  }
  const v = option.value ?? option.value_label ?? option.label;
  return v != null ? String(v).trim() : '';
}

/**
 * Resolve stored value to canonical option key for Form.Select binding.
 */
export function resolvePicklistSelectValue(options, storedValue) {
  if (storedValue == null || storedValue === '') {
    return '';
  }
  const match = findPicklistOptionByValue(options, storedValue);
  if (match) {
    return picklistOptionStorageKey(match);
  }
  return String(storedValue).trim();
}

/** Resolve semicolon-separated multipicklist value to canonical option keys. */
export function resolveMultipicklistSelectedKeys(options, storedValue) {
  if (!storedValue) {
    return [];
  }
  return String(storedValue)
    .split(';')
    .filter((v) => v.trim() !== '')
    .map((p) => resolvePicklistSelectValue(options, p.trim()) || p.trim())
    .filter(Boolean);
}

/** Case-insensitive check if a list of keys includes needle. */
export function picklistKeyListIncludes(keys, needle) {
  const n = normalizePicklistCompareKey(needle);
  if (!n) {
    return false;
  }
  return (keys || []).some((k) => normalizePicklistCompareKey(k) === n);
}

/** Filter dependency rows where controlling_value_key matches (case-insensitive). */
export function filterDependencyRowsByControllingValue(valueDependencies, controllingValue) {
  const cv = normalizePicklistCompareKey(controllingValue);
  if (!cv || !valueDependencies?.length) {
    return [];
  }
  return valueDependencies.filter(
    (vd) => normalizePicklistCompareKey(vd.controlling_value_key) === cv
  );
}

/** Filter picklist options whose keys appear in dependentValueKeys (case-insensitive). */
export function filterPicklistOptionsByKeys(options, dependentValueKeys) {
  if (!options?.length || !dependentValueKeys?.length) {
    return [];
  }
  const normalizedKeys = new Set(
    dependentValueKeys.map((k) => normalizePicklistCompareKey(k)).filter(Boolean)
  );
  return options.filter((pv) => {
    const optionTokens = picklistOptionTokens(pv);
    return optionTokens.some((token) => normalizedKeys.has(token));
  });
}

/** Keep multipicklist parts that exist in availableKeys (case-insensitive). */
export function filterMultipicklistByAvailableKeys(storedValue, availableKeys) {
  const parts = String(storedValue || '')
    .split(';')
    .filter((v) => v.trim() !== '');
  const valid = parts.filter((p) => picklistKeyListIncludes(availableKeys, p.trim()));
  return valid.join(';');
}

/**
 * Tokens for a stored controlling picklist value (key, label, resolved canonical key).
 * Used to match picklist dependency rows case-insensitively.
 */
export function resolveDependencyControllingTokens(controllingOptions, controllingValue) {
  const tokens = new Set();
  const add = (v) => {
    const n = normalizePicklistCompareKey(v);
    if (n) {
      tokens.add(n);
    }
  };
  if (controllingValue == null || controllingValue === '') {
    return tokens;
  }
  const parts = String(controllingValue).includes(';')
    ? String(controllingValue).split(';').filter((v) => v.trim() !== '')
    : [String(controllingValue)];
  parts.forEach((part) => {
    const trimmed = String(part).trim();
    if (!trimmed) {
      return;
    }
    add(trimmed);
    add(resolvePicklistSelectValue(controllingOptions, trimmed));
    const match = findPicklistOptionByValue(controllingOptions, trimmed);
    if (match) {
      picklistOptionTokens(match).forEach(add);
    }
  });
  return tokens;
}

/** Match dependency rows for a controlling field value (key or label, case-insensitive). */
export function filterDependencyRowsByControllingFieldValue(
  valueDependencies,
  controllingOptions,
  controllingValue
) {
  if (!valueDependencies?.length) {
    return [];
  }
  const tokens = resolveDependencyControllingTokens(controllingOptions, controllingValue);
  if (!tokens.size) {
    return [];
  }
  return valueDependencies.filter((vd) =>
    tokens.has(normalizePicklistCompareKey(vd.controlling_value_key))
  );
}

/**
 * Normalize picklist/multipicklist form values to canonical option keys after options load.
 * @returns {Record<string, string>} fieldName -> canonical value updates
 */
export function buildNormalizedPicklistFieldUpdates(allFields, picklistValuesMap, sourceValues) {
  const updates = {};
  if (!allFields?.length || !sourceValues || !picklistValuesMap) {
    return updates;
  }
  allFields.forEach((f) => {
    const fn = f?.fieldName;
    if (!fn) {
      return;
    }
    const opts = picklistValuesMap[fn];
    if (!opts?.length) {
      return;
    }
    const raw = sourceValues[fn];
    if (raw == null || raw === '') {
      return;
    }
    const t = String(f.type || '').toLowerCase();
    if (t === 'picklist') {
      const match = findPicklistOptionByValue(opts, raw);
      if (match) {
        updates[fn] = picklistOptionStorageKey(match);
      }
    } else if (t === 'multipicklist') {
      const resolved = resolveMultipicklistSelectedKeys(opts, raw).join(';');
      if (resolved) {
        updates[fn] = resolved;
      }
    }
  });
  return updates;
}

/**
 * Resolve a record/picklist value to the canonical column key from picklist options.
 * @returns {string|null} matching option.key || option.value, or null
 */
export function resolvePicklistColumnKey(options, recordValue) {
  const match = findPicklistOptionByValue(options, recordValue);
  if (match) {
    return match.key || match.value_key || match.value || null;
  }
  return null;
}
